/**
 * Types pour les messages WebSocket
 * Alignés sur le contrat backend
 */

/**
 * Résultat d'un job complété (export Excel)
 */
export interface U2000JobCompletedResult {
  fileUrl?: string;
  [key: string]: unknown; // Permet d'autres champs dans result
}

/**
 * Message WebSocket pour un job complété
 * Émis par le backend lorsque l'export Excel est terminé (succès ou échec)
 */
export interface U2000JobCompletedMessage {
  type: "job:completed";
  jobId: string;
  status: "completed" | "failed";
  result?: U2000JobCompletedResult;
  errorMessage?: string;
}

/**
 * Message WebSocket pour une nouvelle notification
 * Émis par le backend lorsqu'une nouvelle notification est créée
 * 
 * Structure : Le message a type: "notification:new" au niveau racine (type d'événement WebSocket)
 * Le payload contient le type de notification dans un champ séparé pour éviter le conflit.
 * 
 * IMPORTANT : Comme emitToUser ajoute type: "notification:new" en dernier, le backend DOIT utiliser
 * un nom différent pour le type de notification dans le payload (ex: "notificationType" ou "typeNotification").
 * Si le backend utilise "type" dans le payload, il sera écrasé par type: "notification:new".
 * 
 * NOTE : userId est optionnel car le message est déjà envoyé à l'utilisateur spécifique via emitToUser.
 * Il peut être ajouté par le contexte d'authentification côté frontend si nécessaire.
 */
export interface U2000NotificationMessage {
  type: "notification:new"; // Type d'événement WebSocket (ajouté en dernier par emitToUser, écrase tout champ "type" du payload)
  id: string;
  version?: number; // Optionnel car pas toujours nécessaire dans le message WebSocket
  userId?: string; // Optionnel car le message est déjà ciblé via emitToUser
  organizationId?: string;
  notificationType?: "system" | "message" | "alert" | "task"; // Type de notification (recommandé : backend devrait utiliser ce champ)
  typeNotification?: "system" | "message" | "alert" | "task"; // Type de notification (alternative : si backend utilise ce champ)
  importance: "info" | "success" | "warning" | "error" | "critical";
  title: string;
  message: string;
  timestamp: string; // ISO 8601
  isRead: boolean;
  fileUrl?: string;
  /** Type MIME du fichier (ex. application/pdf) pour afficher le bouton « Ouvrir dans un nouvel onglet » même si fileUrl n’a pas d’extension. */
  fileMimeType?: string;
  linkUrl?: string;
  metadata?: Record<string, unknown>;
}

/** jobType U3134 pour le calcul asynchrone des travaux en cours */
export const U3134_GENERATE_WIP_JOB_TYPE = "U3134_GENERATE_WIP";

export interface U3134GenerateWipProgressMessage {
  type: "job:progress";
  jobId: string;
  jobType?: string;
  phase?: string;
  current?: number;
  total?: number;
  currentItem?: {
    timesheetId?: string;
    employeeName?: string;
    periodDate?: string;
  };
  lastResult?: {
    timesheetId?: string;
    employeeName?: string;
    status?: "success" | "failed" | string;
  };
  counters?: {
    promoted?: number;
    processed?: number;
    succeeded?: number;
    failed?: number;
  };
}

/** jobType envoyé dans metadata pour la notification de fin de job fetch property index U3141 */
export const U3141_FETCH_PROPERTY_INDEX_JOB_TYPE = "U3141_FETCH_PROPERTY_INDEX";

/** jobType envoyé dans metadata pour la notification de fin de job fusion PDF chaîne annotée U3141 */
export const U3141_MERGE_ANNOTATED_LOT_CHAIN_INDEX_PDFS_JOB_TYPE = "U3141_MERGE_ANNOTATED_LOT_CHAIN_INDEX_PDFS";

/** jobType envoyé dans metadata pour la notification de fin de job historique cadastral U3141 (nouveau nom backend) */
export const U3141_FETCH_CADASTRAL_HISTORY_JOB_TYPE = "U3141_FETCH_CADASTRAL_HISTORY";

/** @deprecated Utiliser U3141_FETCH_CADASTRAL_HISTORY_JOB_TYPE. Conservé pour rétrocompatibilité. */
export const U3141_FETCH_HISTORIQUE_CADASTRAL_JOB_TYPE = "U3141_FETCH_HISTORIQUE_CADASTRAL";

/** jobType envoyé dans metadata pour la notification de fin de job reconstitution chaîne depuis PDF (BL-0562) */
export const U3141_RECONSTITUTE_CHAIN_FROM_PDF_JOB_TYPE = "U3141_RECONSTITUTE_CHAIN_FROM_PDF";
export const U3141_IMPORT_CHAIN_ANNOTATIONS_FROM_PDF_JOB_TYPE = "U3141_IMPORT_CHAIN_ANNOTATIONS_FROM_PDF";

/** jobType envoyé dans metadata pour la notification de fin de job import registre foncier U3141 */
export const U3141_IMPORT_FROM_LAND_REGISTER_JOB_TYPE = "U3141_IMPORT_FROM_LAND_REGISTER";

/** jobType envoyé dans metadata pour la notification de fin d'export Excel liste registres fonciers U3141 */
export const U3141_LAND_REGISTER_EXCEL_EXPORT_JOB_TYPE = "U3141_LAND_REGISTER_EXCEL_EXPORT";

/** jobType pour notification:new — export Excel liste approbation feuilles de temps U3130 (aligner le backend sur cette valeur si besoin) */
export const U3130_TIMESHEET_APPROVAL_EXCEL_EXPORT_JOB_TYPE = "U3130_TIMESHEET_APPROVAL_EXCEL_EXPORT";

/** jobType pour notification:new — PDF sommaire paie U3130 (pas d'auto-téléchargement ; même mode que U3121 PDF) */
export const U3130_TIMESHEET_SOMMAIRE_PAIE_PDF_EXPORT_JOB_TYPE = "U3130_TIMESHEET_SOMMAIRE_PAIE_PDF_EXPORT";

/** @deprecated Alias historique — préférer U3130_TIMESHEET_SOMMAIRE_PAIE_PDF_EXPORT */
export const U3130_TIMESHEET_APPROVAL_SOMMAIRE_PAIE_PDF_JOB_TYPE = U3130_TIMESHEET_SOMMAIRE_PAIE_PDF_EXPORT_JOB_TYPE;

/** jobType enregistré dans le job U2200 — export Excel Employeur D U3130 */
export const U3130_TIMESHEET_EMPLOYEUR_D_EXCEL_EXPORT_JOB_TYPE = "U3130_TIMESHEET_EMPLOYEUR_D_EXCEL_EXPORT";

/** jobType enregistré dans le job U2200 — PDF dépenses par semaine par code U3130 (aligner sur le backend) */
export const U3130_TIMESHEET_DEPENSES_PAR_SEMAINE_PDF_EXPORT_JOB_TYPE = "U3130_TIMESHEET_DEPENSES_PAR_SEMAINE_PDF_EXPORT";

/** jobType enregistré dans le job U2200 — export Excel rapport âge TEC U3134 (BL-0027) */
export const U3134_TEC_AGE_REPORT_EXCEL_JOB_TYPE = "U3134_TEC_AGE_REPORT";

/** jobType enregistré dans le job U2200 — export PDF rapport âge TEC U3134 (BL-0027) */
export const U3134_TEC_AGE_REPORT_PDF_JOB_TYPE = "U3134_TEC_AGE_REPORT_PDF";

/** jobType enregistré dans le job U2200 — export PDF rapport CAR U3125 (BL-0028) */
export const U3125_AR_AGING_REPORT_PDF_JOB_TYPE = "U3125_AR_AGING_REPORT_PDF";

/** jobType enregistré dans le job U2200 — export Excel rapport CAR U3125 (BL-0028) */
export const U3125_AR_AGING_REPORT_EXCEL_JOB_TYPE = "U3125_AR_AGING_REPORT";

/** jobType U2200 — export Excel liste projets U3121 (BL-0414) */
export const U3121_PROJECT_EXCEL_EXPORT_JOB_TYPE = "U3121_PROJECT_EXCEL_EXPORT";

/** jobType U2200 — export Excel liste TEC U3134 (BL-0414) */
export const U3134_WIP_LINE_EXCEL_EXPORT_JOB_TYPE = "U3134_WIP_LINE_EXCEL_EXPORT";

/** jobType U2200 — export Excel liste factures U3125 (BL-0414) */
export const U3125_INVOICE_EXCEL_EXPORT_JOB_TYPE = "U3125_INVOICE_EXCEL_EXPORT";

/** jobType enregistré dans le job U2200 — export Excel chat IA U3204 (BL-0140) */
export const U3204_AI_EXCEL_EXPORT_JOB_TYPE = "U3204_AI_EXCEL_EXPORT";

/** jobType envoyé dans metadata pour la notification de fin d'export PDF formulaire ouverture projet U3121 */
export const U3121_PROJECT_OPENING_FORM_PDF_EXPORT_JOB_TYPE = "U3121_PROJECT_OPENING_FORM_PDF_EXPORT";

/** Événement WebSocket dédié U3121 — sync Google Calendar (hors centre de notifications U2105). */
export const U3121_GOOGLE_CALENDAR_SYNC_WS_EVENT = "u3121:googleCalendarSync";

/** Onglet U3121 « Projet à arpenter » — filtre côté UI pour la notification sync calendrier. */
export const U3121_PROJECT_TO_SURVEY_TAB_ID = "tabProjectToSurvey";

export type U3121GoogleCalendarSyncWsStatus = "SYNCED" | "FAILED" | "PENDING" | "DELETED";

/** BL-0629 — écart date Google vs échéance projet (WebSocket only). */
export type U3121GoogleCalendarDateDivergenceKind = "mismatch" | "missing" | "notAllDay";

export interface U3121GoogleCalendarDateDivergence {
  kind: U3121GoogleCalendarDateDivergenceKind;
  googleDate: string | null;
  projectDueDate: string;
  message: string;
}

export interface U3121GoogleCalendarSyncWsMessage {
  type: typeof U3121_GOOGLE_CALENDAR_SYNC_WS_EVENT;
  organizationId: string;
  projectId: string;
  subProjectId: string | null;
  projectToSurveyId: string;
  action: "upsert" | "delete";
  googleCalendarSync: {
    status: U3121GoogleCalendarSyncWsStatus;
    lastSyncAt: string | null;
    lastError: string | null;
  } | null;
  /** null = pas d'écart ; absent sur anciens messages = traiter comme null. */
  dateDivergence?: U3121GoogleCalendarDateDivergence | null;
  /** Après action Synchroniser : nouvelle échéance à fusionner dans le formulaire. */
  projectDueDate?: string | null;
}

/** Payload utile pour fusion formulaire (hook U3121). */
export type U3121GoogleCalendarSyncWsFormUpdate = {
  googleCalendarSync: U3121GoogleCalendarSyncWsMessage["googleCalendarSync"];
  dateDivergence: U3121GoogleCalendarDateDivergence | null | undefined;
  projectDueDate?: string | null;
};

/** Événement WebSocket dédié U3141 — progression upload plans (hors centre U2105, BL-0603). */
export const U3141_PLAN_UPLOAD_PROGRESS_WS_EVENT = "u3141:planUploadProgress";

export type U3141PlanUploadProgressWsPhase = "started" | "file" | "done" | "failed";

interface U3141PlanUploadProgressWsBase {
  type: typeof U3141_PLAN_UPLOAD_PROGRESS_WS_EVENT;
  clientRequestUuid: string;
  masterLotChainId: string;
  organizationId: string;
}

export interface U3141PlanUploadProgressWsStartedMessage extends U3141PlanUploadProgressWsBase {
  phase: "started";
  total: number;
  zipArchiveCount: number;
}

export interface U3141PlanUploadProgressWsFileMessage extends U3141PlanUploadProgressWsBase {
  phase: "file";
  current: number;
  total: number;
  entryPath: string;
  archiveName: string;
  documentId?: string;
  error?: string;
}

export interface U3141PlanUploadProgressWsDoneMessage extends U3141PlanUploadProgressWsBase {
  phase: "done";
  successCount: number;
  errorCount: number;
  anySuccess: boolean;
}

export interface U3141PlanUploadProgressWsFailedMessage extends U3141PlanUploadProgressWsBase {
  phase: "failed";
  message: string;
}

export type U3141PlanUploadProgressWsMessage =
  | U3141PlanUploadProgressWsStartedMessage
  | U3141PlanUploadProgressWsFileMessage
  | U3141PlanUploadProgressWsDoneMessage
  | U3141PlanUploadProgressWsFailedMessage;

/** Événements WebSocket U3204 — streaming LLM chat IA */
export const U3204_LLM_TOKEN_WS_EVENT = "llm:token";
export const U3204_LLM_THINKING_WS_EVENT = "llm:thinking";
export const U3204_LLM_DONE_WS_EVENT = "llm:done";
export const U3204_LLM_ERROR_WS_EVENT = "llm:error";
export const U3204_LLM_TOOL_START_WS_EVENT = "llm:tool_start";
export const U3204_LLM_TOOL_DONE_WS_EVENT = "llm:tool_done";
export const U3204_LLM_CONVERSATION_TITLE_WS_EVENT = "llm:conversation_title";
/** Export Excel prêt — carte téléchargement dans le fil (BL-0166) */
export const U3204_LLM_EXPORT_READY_WS_EVENT = "llm:export_ready";

export interface U3204LlmTokenMessage {
  type: typeof U3204_LLM_TOKEN_WS_EVENT;
  conversationId: string;
  messageId: string;
  delta: string;
}

export interface U3204LlmThinkingMessage {
  type: typeof U3204_LLM_THINKING_WS_EVENT;
  conversationId: string;
  messageId: string;
  delta: string;
}

/** Citation doc produit (RAG) sur llm:done — BL-0200 */
export interface U3204LlmDoneSource {
  kind: 'product';
  chunkId?: string;
  logicalUnitCode: string;
  sectionPath: string;
  sectionTitle: string;
  url: string;
  score?: number;
}

export interface U3204LlmDoneMessage {
  type: typeof U3204_LLM_DONE_WS_EVENT;
  conversationId: string;
  messageId: string;
  usage?: { inputTokens: number; outputTokens: number };
  /** Sources RAG — affichage bloc Sources en fin de stream (BL-0200) */
  sources?: U3204LlmDoneSource[] | null;
}

export interface U3204LlmErrorMessage {
  type: typeof U3204_LLM_ERROR_WS_EVENT;
  conversationId: string;
  messageId?: string;
  errorCode: string;
  message: string;
}

export interface U3204LlmToolStartMessage {
  type: typeof U3204_LLM_TOOL_START_WS_EVENT;
  conversationId: string;
  messageId: string;
  toolName: string;
}

export interface U3204LlmToolDoneMessage {
  type: typeof U3204_LLM_TOOL_DONE_WS_EVENT;
  conversationId: string;
  messageId: string;
  toolName: string;
  status: "success" | "error";
}

export interface U3204LlmConversationTitleMessage {
  type: typeof U3204_LLM_CONVERSATION_TITLE_WS_EVENT;
  conversationId: string;
  title: string;
  version: number;
}

export interface U3204LlmExportReadyMessage {
  type: typeof U3204_LLM_EXPORT_READY_WS_EVENT;
  conversationId: string;
  messageId: string;
  fileUrl: string;
  fileName: string;
  fileMimeType: string;
  jobId?: string;
}

/**
 * Type union pour tous les types de messages WebSocket
 * Permet d'étendre facilement avec d'autres types de messages à l'avenir
 */
export type U2000WebSocketMessage =
  | U2000JobCompletedMessage
  | U2000NotificationMessage
  | U3134GenerateWipProgressMessage
  | U3121GoogleCalendarSyncWsMessage
  | U3141PlanUploadProgressWsMessage
  | U3204LlmTokenMessage
  | U3204LlmThinkingMessage
  | U3204LlmDoneMessage
  | U3204LlmErrorMessage
  | U3204LlmToolStartMessage
  | U3204LlmToolDoneMessage
  | U3204LlmConversationTitleMessage
  | U3204LlmExportReadyMessage;

/**
 * Type guard pour vérifier si un message est de type job:completed
 */
export function isJobCompletedMessage(message: unknown): message is U2000JobCompletedMessage {
  return (
    typeof message === "object" &&
    message !== null &&
    "type" in message &&
    (message as U2000JobCompletedMessage).type === "job:completed" &&
    "jobId" in message &&
    typeof (message as U2000JobCompletedMessage).jobId === "string"
  );
}

/**
 * Type guard pour vérifier si un message est de type notification:new
 * Note: userId est optionnel car le message est déjà ciblé via emitToUser
 */
export function isNotificationMessage(message: unknown): message is U2000NotificationMessage {
  const isValid = (
    typeof message === "object" &&
    message !== null &&
    "type" in message &&
    (message as U2000NotificationMessage).type === "notification:new" &&
    "id" in message &&
    typeof (message as U2000NotificationMessage).id === "string" &&
    "title" in message &&
    typeof (message as U2000NotificationMessage).title === "string" &&
    "message" in message &&
    typeof (message as U2000NotificationMessage).message === "string"
  );
  
  if (import.meta.env.DEV && !isValid) {
    console.warn("[Type Guard] Message WebSocket notification:new invalide:", message);
  }
  
  return isValid;
}

/**
 * Type guard pour vérifier si un message est de type job:progress
 */
export function isU3134GenerateWipProgressMessage(message: unknown): message is U3134GenerateWipProgressMessage {
  return (
    typeof message === "object" &&
    message !== null &&
    "type" in message &&
    (message as U3134GenerateWipProgressMessage).type === "job:progress" &&
    "jobId" in message &&
    typeof (message as U3134GenerateWipProgressMessage).jobId === "string"
  );
}

export function isU3121GoogleCalendarSyncMessage(message: unknown): message is U3121GoogleCalendarSyncWsMessage {
  return (
    typeof message === "object" &&
    message !== null &&
    "type" in message &&
    (message as U3121GoogleCalendarSyncWsMessage).type === U3121_GOOGLE_CALENDAR_SYNC_WS_EVENT &&
    "projectId" in message &&
    typeof (message as U3121GoogleCalendarSyncWsMessage).projectId === "string" &&
    "projectToSurveyId" in message &&
    typeof (message as U3121GoogleCalendarSyncWsMessage).projectToSurveyId === "string"
  );
}

function u3141HasPlanUploadProgressBase(message: Record<string, unknown>): boolean {
  return (
    message.type === U3141_PLAN_UPLOAD_PROGRESS_WS_EVENT &&
    typeof message.clientRequestUuid === "string" &&
    typeof message.masterLotChainId === "string" &&
    typeof message.organizationId === "string" &&
    typeof message.phase === "string"
  );
}

export function isU3141PlanUploadProgressMessage(message: unknown): message is U3141PlanUploadProgressWsMessage {
  if (typeof message !== "object" || message === null || !("type" in message)) {
    return false;
  }
  const m = message as Record<string, unknown>;
  if (!u3141HasPlanUploadProgressBase(m)) {
    return false;
  }
  switch (m.phase) {
    case "started":
      return typeof m.total === "number" && typeof m.zipArchiveCount === "number";
    case "file":
      return (
        typeof m.current === "number" &&
        typeof m.total === "number" &&
        typeof m.entryPath === "string" &&
        typeof m.archiveName === "string"
      );
    case "done":
      return (
        typeof m.successCount === "number" &&
        typeof m.errorCount === "number" &&
        typeof m.anySuccess === "boolean"
      );
    case "failed":
      return typeof m.message === "string" && m.message.length > 0;
    default:
      return false;
  }
}

export function isU3204LlmTokenMessage(message: unknown): message is U3204LlmTokenMessage {
  return (
    typeof message === "object" &&
    message !== null &&
    "type" in message &&
    (message as U3204LlmTokenMessage).type === U3204_LLM_TOKEN_WS_EVENT &&
    "conversationId" in message &&
    typeof (message as U3204LlmTokenMessage).conversationId === "string" &&
    "messageId" in message &&
    typeof (message as U3204LlmTokenMessage).messageId === "string" &&
    "delta" in message &&
    typeof (message as U3204LlmTokenMessage).delta === "string"
  );
}

export function isU3204LlmThinkingMessage(message: unknown): message is U3204LlmThinkingMessage {
  return (
    typeof message === "object" &&
    message !== null &&
    "type" in message &&
    (message as U3204LlmThinkingMessage).type === U3204_LLM_THINKING_WS_EVENT &&
    "conversationId" in message &&
    typeof (message as U3204LlmThinkingMessage).conversationId === "string"
  );
}

export function isU3204LlmDoneMessage(message: unknown): message is U3204LlmDoneMessage {
  return (
    typeof message === "object" &&
    message !== null &&
    "type" in message &&
    (message as U3204LlmDoneMessage).type === U3204_LLM_DONE_WS_EVENT &&
    "conversationId" in message &&
    typeof (message as U3204LlmDoneMessage).conversationId === "string" &&
    "messageId" in message &&
    typeof (message as U3204LlmDoneMessage).messageId === "string"
  );
}

export function isU3204LlmErrorMessage(message: unknown): message is U3204LlmErrorMessage {
  return (
    typeof message === "object" &&
    message !== null &&
    "type" in message &&
    (message as U3204LlmErrorMessage).type === U3204_LLM_ERROR_WS_EVENT &&
    "conversationId" in message &&
    typeof (message as U3204LlmErrorMessage).conversationId === "string" &&
    "errorCode" in message &&
    typeof (message as U3204LlmErrorMessage).errorCode === "string"
  );
}

export function isU3204LlmToolStartMessage(message: unknown): message is U3204LlmToolStartMessage {
  return (
    typeof message === "object" &&
    message !== null &&
    "type" in message &&
    (message as U3204LlmToolStartMessage).type === U3204_LLM_TOOL_START_WS_EVENT &&
    "conversationId" in message &&
    typeof (message as U3204LlmToolStartMessage).conversationId === "string" &&
    "messageId" in message &&
    typeof (message as U3204LlmToolStartMessage).messageId === "string" &&
    "toolName" in message &&
    typeof (message as U3204LlmToolStartMessage).toolName === "string"
  );
}

export function isU3204LlmToolDoneMessage(message: unknown): message is U3204LlmToolDoneMessage {
  return (
    typeof message === "object" &&
    message !== null &&
    "type" in message &&
    (message as U3204LlmToolDoneMessage).type === U3204_LLM_TOOL_DONE_WS_EVENT &&
    "conversationId" in message &&
    typeof (message as U3204LlmToolDoneMessage).conversationId === "string" &&
    "messageId" in message &&
    typeof (message as U3204LlmToolDoneMessage).messageId === "string" &&
    "toolName" in message &&
    typeof (message as U3204LlmToolDoneMessage).toolName === "string" &&
    "status" in message &&
    ((message as U3204LlmToolDoneMessage).status === "success" ||
      (message as U3204LlmToolDoneMessage).status === "error")
  );
}

export function isU3204LlmConversationTitleMessage(
  message: unknown,
): message is U3204LlmConversationTitleMessage {
  return (
    typeof message === "object" &&
    message !== null &&
    "type" in message &&
    (message as U3204LlmConversationTitleMessage).type === U3204_LLM_CONVERSATION_TITLE_WS_EVENT &&
    "conversationId" in message &&
    typeof (message as U3204LlmConversationTitleMessage).conversationId === "string" &&
    "title" in message &&
    typeof (message as U3204LlmConversationTitleMessage).title === "string" &&
    "version" in message &&
    typeof (message as U3204LlmConversationTitleMessage).version === "number"
  );
}

export function isU3204LlmExportReadyMessage(message: unknown): message is U3204LlmExportReadyMessage {
  return (
    typeof message === "object" &&
    message !== null &&
    "type" in message &&
    (message as U3204LlmExportReadyMessage).type === U3204_LLM_EXPORT_READY_WS_EVENT &&
    "conversationId" in message &&
    typeof (message as U3204LlmExportReadyMessage).conversationId === "string" &&
    "messageId" in message &&
    typeof (message as U3204LlmExportReadyMessage).messageId === "string" &&
    "fileUrl" in message &&
    typeof (message as U3204LlmExportReadyMessage).fileUrl === "string" &&
    "fileName" in message &&
    typeof (message as U3204LlmExportReadyMessage).fileName === "string" &&
    "fileMimeType" in message &&
    typeof (message as U3204LlmExportReadyMessage).fileMimeType === "string"
  );
}
